import { ofetch } from "ofetch";
import { BarkConfig, BarkOptions, BarkResponse, PushRequest } from "./types.js";

/**
 * Bark推送客户端
 */
export class BarkClient {
  private readonly serverUrl: string;
  private readonly deviceKey: string;
  private readonly defaultOptions: Partial<BarkOptions>;
  private readonly httpClient: typeof ofetch;

  /**
   * 创建Bark客户端实例
   * @param config 客户端配置
   */
  constructor(config: BarkConfig) {
    this.serverUrl = config.serverUrl || "https://api.day.app";
    this.deviceKey = config.deviceKey;
    this.defaultOptions = config.defaultOptions || {};

    this.httpClient = ofetch.create({
      baseURL: this.serverUrl,
      timeout: 10000,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  /**
   * 发送推送消息
   * @param options 推送选项
   * @returns Promise<BarkResponse>
   */
  async push(options: BarkOptions): Promise<BarkResponse> {
    const mergedOptions = { ...this.defaultOptions, ...options };

    const request: PushRequest = {
      device_key: this.deviceKey,
      body: mergedOptions.body,
      ...(mergedOptions.title && { title: mergedOptions.title }),
      ...(mergedOptions.group && { group: mergedOptions.group }),
      ...(mergedOptions.icon && { icon: mergedOptions.icon }),
      ...(mergedOptions.sound && { sound: mergedOptions.sound }),
      ...(mergedOptions.url && { url: mergedOptions.url }),
      ...(mergedOptions.automaticallyCopy !== undefined && {
        automaticallyCopy: mergedOptions.automaticallyCopy,
      }),
      ...(mergedOptions.copy !== undefined && { copy: mergedOptions.copy }),
      ...(mergedOptions.level && { level: mergedOptions.level }),
      ...(mergedOptions.badge !== undefined && { badge: mergedOptions.badge }),
      ...(mergedOptions.isArchive !== undefined && {
        isArchive: mergedOptions.isArchive,
      }),
    };

    try {
      const response = await this.httpClient<BarkResponse>("/push", {
        method: "POST",
        body: request,
      });
      return response;
    } catch (error: any) {
      if (error.response && error.response._data) {
        throw new Error(
          `Bark推送失败: ${error.response._data?.message || error.message}`
        );
      }
      throw new Error(`Bark推送失败: ${error.message || "未知错误"}`);
    }
  }

  /**
   * 发送简单文本消息
   * @param text 消息内容
   * @param title 消息标题（可选）
   * @returns Promise<BarkResponse>
   */
  async sendText(text: string, title?: string): Promise<BarkResponse> {
    return this.push({
      body: text,
      ...(title && { title }),
    });
  }

  /**
   * 发送带链接的消息
   * @param text 消息内容
   * @param url 链接地址
   * @param title 消息标题（可选）
   * @returns Promise<BarkResponse>
   */
  async sendLink(
    text: string,
    url: string,
    title?: string
  ): Promise<BarkResponse> {
    return this.push({
      body: text,
      url,
      ...(title && { title }),
    });
  }

  /**
   * 发送带自定义声音的消息
   * @param text 消息内容
   * @param sound 声音名称
   * @param title 消息标题（可选）
   * @returns Promise<BarkResponse>
   */
  async sendWithSound(
    text: string,
    sound: string,
    title?: string
  ): Promise<BarkResponse> {
    return this.push({
      body: text,
      sound,
      ...(title && { title }),
    });
  }

  /**
   * 发送重要消息（时间敏感级别）
   * @param text 消息内容
   * @param title 消息标题（可选）
   * @returns Promise<BarkResponse>
   */
  async sendUrgent(text: string, title?: string): Promise<BarkResponse> {
    return this.push({
      body: text,
      level: "timeSensitive",
      ...(title && { title }),
    });
  }

  /**
   * 发送静默消息（被动级别）
   * @param text 消息内容
   * @param title 消息标题（可选）
   * @returns Promise<BarkResponse>
   */
  async sendSilent(text: string, title?: string): Promise<BarkResponse> {
    return this.push({
      body: text,
      level: "passive",
      ...(title && { title }),
    });
  }

  /**
   * 测试推送（发送测试消息）
   * @returns Promise<BarkResponse>
   */
  async test(): Promise<BarkResponse> {
    return this.sendText("Bark SDK 测试消息", "Bark SDK");
  }

  /**
   * 获取当前配置的设备密钥
   * @returns string
   */
  getDeviceKey(): string {
    return this.deviceKey;
  }

  /**
   * 获取当前配置的服务器地址
   * @returns string
   */
  getServerUrl(): string {
    return this.serverUrl;
  }
}
